import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { exportDocumentToBlob } from '../utils/exportDocument';
import { downloadBlob } from '../utils/downloadBlob';

export const CONFIRM_DIALOGS = {
  RESET: 'reset',
  OPEN: 'open',
};

const toSerializableElements = (elements) =>
  elements.map((element) => {
    const serializable = { ...element };
    delete serializable.image;
    return serializable;
  });

const useFileActions = ({ editor, notice, viewport, saveActions }) => {
  const { t } = useTranslation('mediaStudio');
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const loadDocument = async (file) => {
    const { containerSize } = viewport;
    const applied = await notice.run(
      () => editor.loadBaseImage(file, containerSize.width ? containerSize : undefined),
      'loadFailed',
    );
    if (applied) {
      viewport.release();
    }
  };

  const openImage = (file) => {
    if (editor.hasDocument) {
      setConfirmDialog({ type: CONFIRM_DIALOGS.OPEN, file });
      return;
    }
    loadDocument(file);
  };

  const confirm = () => {
    setConfirmDialog(null);
    if (confirmDialog?.type === CONFIRM_DIALOGS.OPEN) {
      loadDocument(confirmDialog.file);
    }
    if (confirmDialog?.type === CONFIRM_DIALOGS.RESET) {
      editor.reset();
    }
  };

  const save = async (action) => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    await notice.run(async () => {
      const blob = await exportDocumentToBlob(editor);
      await action.handler(blob, {
        elements: toSerializableElements(editor.elements),
        width: editor.width,
        height: editor.height,
      });
    }, 'exportFailed');
    setIsSaving(false);
  };

  return {
    isSaving,
    confirmDialog,
    saveActions: saveActions.map((action) => ({
      ...action,
      label: action.label || (action.labelKey ? t(action.labelKey) : action.id),
    })),
    openImage,
    addLayer: (source, options) => notice.run(() => editor.addImage(source, options), 'loadFailed'),
    download: () =>
      notice.run(async () => {
        downloadBlob(await exportDocumentToBlob(editor), `media-studio-${Date.now()}.png`);
      }, 'exportFailed'),
    save,
    requestReset: () => setConfirmDialog({ type: CONFIRM_DIALOGS.RESET }),
    closeConfirm: () => setConfirmDialog(null),
    confirm,
  };
};

export default useFileActions;
